/**
 * Matches a protocol (e.g. http:, https:, ftp:, mailto:, etc.)
 */
const PROTOCOL_REGEX = /^[a-z][a-z0-9\+\-\.]+\:/i;

export function parseUrlObject(src: string): UrlWithRelativePath | null {
  if (!src) {
    return null;
  }
  try {
    const publicURL = (process.env.PUBLIC_URL as string) || 'https://example.com';

    // If it's a fully qualified url, use it as is, otherwise include the public url

    if (src.match(PROTOCOL_REGEX)) return new URL(src);
    const urlWithDummyDomain = new URL(src, new URL(publicURL));
    const removeLeadingSlash = src.startsWith('#') || src.startsWith('?');
    return new UrlWithRelativePath(urlWithDummyDomain, removeLeadingSlash);
  } catch {
    return null;
  }
}

/**
 * Checks if a url is external.  Note: We don't use window.location.origin because it's not available in the server side
 * and will cause hydration errors.
 * @param url The url to check
 * @returns True if the url is external, false otherwise
 */
export function isExternalUrl(url: string): boolean {
  const hasProtocol = !!url.match(PROTOCOL_REGEX);

  const publicURL = process.env.PUBLIC_URL as string | undefined;

  // Preview deployments won't have a public url, so just check whether it has a protocol
  if (!publicURL && process.env.VERCEL_TARGET_ENV === 'preview') {
    return hasProtocol;
  }
  return hasProtocol && !url.startsWith(publicURL ?? '');
}

export class UrlWithRelativePath {
  href: string;
  pathname: string;
  protocol: string;
  origin?: string;
  search: string;
  hash: string;

  constructor(url: URL, removeLeadingSlash: boolean = false) {
    this.href = url.href.replace(url.origin, '');
    if (removeLeadingSlash) {
      this.href = this.href.replace(/^\//, '');
    }
    this.pathname = url.pathname;
    this.protocol = '';
    this.search = url.search;
    this.hash = url.hash;
  }
  toString() {
    return this.href;
  }
  toJSON() {
    return this.href;
  }
}

// Validate GUID format
export function isGuid(value: string | undefined) {
  if (!value) return false;
  const guidRegex =
    /^\{?[0-9a-fA-F]{8}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{12}\}?$/;
  return guidRegex.test(value);
}

export function normalizeGuid(value: string | undefined) {
  if (value && isGuid(value)) {
    const strippedGuid = value.replace(/[\{\-\}]/g, '').toLowerCase();
    const formattedGuid = `${strippedGuid.slice(0, 8)}-${strippedGuid.slice(8, 12)}-${strippedGuid.slice(12, 16)}-${strippedGuid.slice(16, 20)}-${strippedGuid.slice(20, 32)}`;
    return formattedGuid;
  }
  return null;
}
/*
Replace tokens in the given text with respective values
*/
export interface ReplacementToken {
  key: string;
  value: string;
}

export function tokenReplace(text: string | undefined, tokens: ReplacementToken[] | undefined) {
  // Replace each tokens
  if (text && tokens && tokens.length > 0) {
    tokens?.forEach((token) => {
      text = text?.replace(token.key, token.value);
    });
  }
  return text || '';
}

/**
 * Capitalizes the first letter of a string
 * @param str The string to capitalize
 * @returns The string with its first letter capitalized
 * @example
 * capitalizeFirstLetter('hello') // returns 'Hello'
 * capitalizeFirstLetter('world') // returns 'World'
 */
export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Trim string to desired length and add elipses
 * @param str The string to truncate
 * @param length The desired length of the string
 * @returns The truncated string
 * @example
 * truncateString('Hello, world!', 5) // returns 'Hello...'
 */
export function truncateString(str: string, length: number) {
  if (str === null || str === '') return '';
  else str = str.toString();

  return str.length > length ? str.substring(0, length) + '...' : str.substring(0, length);
}

/**
 * encodeURIComponent with single quotes as well so behavior
 * is consistent between Node and browsers. This fixes hydration errors.
 * @param str The string to encode
 * @returns The encoded string
 * @example
 * encodeURIComponentSafe("Hello 'world'!") // returns 'Hello%20%27world%27%21'
 */
export function encodeURIComponentSafe(str: string) {
  return encodeURIComponent(str).replace(/'/g, '%27');
}

// Source - https://stackoverflow.com/a/57527468
// Posted by Kamil Kiełczewski, modified by community. See post 'Timeline' for change history
// Retrieved 2026-04-13, License - CC BY-SA 4.0
// 256 caps total pattern length (ReDoS size guard); 50 caps wildcard token count (ReDoS complexity guard).
const MAX_WILDCARD_LENGTH = 256;
const MAX_WILDCARD_TOKENS = 50; // max total * and ? characters allowed

export function wildcardToRegExp(wildcard: string): RegExp {
  // Guard 1: reject unreasonably long input before any processing
  if (wildcard.length > MAX_WILDCARD_LENGTH) {
    throw new Error(`Wildcard pattern exceeds maximum allowed length of ${MAX_WILDCARD_LENGTH}`);
  }
  // Guard 2: reject patterns with too many wildcard tokens (prevents .a.a.a... backtracking via '?')
  const tokenCount = (wildcard.match(/[*?]/g) || []).length;
  if (tokenCount > MAX_WILDCARD_TOKENS) {
    throw new Error(
      `Wildcard pattern exceeds maximum allowed wildcard tokens of ${MAX_WILDCARD_TOKENS}`
    );
  }
  // Collapse consecutive '*' into one to prevent .*.*..* catastrophic backtracking
  const normalized = wildcard.replace(/\*+/g, '*');
  const escaped = normalized.replace(/[.+^${}()|[\]\\]/g, '\\$&'); // regexp escape
  return new RegExp(`^${escaped.replace(/\*/g, '.*').replace(/\?/g, '.')}$`, 'i');
}
